import { Injectable } from '@angular/core';
import { registerLocaleData } from '@angular/common';

import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { of } from 'rxjs';
import { mergeMap, concatMap, withLatestFrom, map, filter } from 'rxjs/operators';
import moment from 'moment';

import { setLocaleAction, setLockedLocaleAction, localeLoadedAction, loadLocaleAction } from './actions';
import { LocaleState } from './state';
import { selectLocaleState } from './selectors';
import { LocaleResolverService } from '../services/locale-resolver/locale-resolver.service';

@Injectable()
export class LocaleEffects {
  setLocale$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setLocaleAction),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(selectLocaleState))))),
      filter(([, /* action */ state]) => !state.isLocked),
      map(([action /* state */]) => {
        return loadLocaleAction({ bcpLocale: action.bcpLocale });
      })
    )
  );

  setLockedLocale$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setLockedLocaleAction),
      map(action => {
        return loadLocaleAction({ bcpLocale: action.bcpLocale });
      })
    )
  );

  loadLocale$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadLocaleAction),
      mergeMap(({ bcpLocale: newLocale }) => {
        const ngLocaleFilename = this.localeResolver.resolveFromBcpToNgFilename(newLocale);
        const localeFile = `@angular/common/locales/${ngLocaleFilename}.js`;

        return import(/* webpackChunkName: "locale-[request]" */ localeFile).then(
          ngLocaleData => {
            registerLocaleData(ngLocaleData.default, newLocale);
            moment.locale(newLocale);

            return localeLoadedAction({ bcpLocale: newLocale });
          }
        );
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<LocaleState>,
    private readonly localeResolver: LocaleResolverService
  ) {}
}
